/**
 * Design Page
 *
 * Appearance and theme customization settings.
 *
 * @package PresszoneComments
 */

import API from '../utils/api.js';
import { el, clear, showLoading, __ } from '../utils/dom.js';
import { SaveButton } from '../components/Button.js';
import { Select } from '../components/Select.js';
import { TileSelect } from '../components/TileSelect.js';
import { Toggle } from '../components/Toggle.js';

// State
let settings = {};
let originalSettings = {};
let saving = false;
let isDirty = false;
let iframeReady = false;
let previewIframe = null;

/**
 * Handle messages from the preview iframe.
 */
function handleIframeMessage(event) {
    // Basic origin check (can be stricter if needed)
    if (event.origin !== window.location.origin) return;

    if (event.data.type === 'presszone_preview_ready') {
        iframeReady = true;
        updateIframePreview();
    }
}

/**
 * Toggle visibility of the Border Thickness setting with slide animation.
 *
 * @param {boolean} show - Whether to show the border thickness setting.
 */
function toggleBorderThicknessVisibility(show) {
    const wrapper = document.getElementById('border-thickness-wrapper');
    if (!wrapper) return;

    if (show) {
        wrapper.classList.remove('presszone-comments--hidden');
    } else {
        wrapper.classList.add('presszone-comments--hidden');
    }
}

/**
 * Render the design page.
 */
export async function renderDesign(container, addNotice) {
    showLoading(container, __('Loading design settings...', 'comments-press-zone'));

    // Setup message listener
    window.removeEventListener('message', handleIframeMessage);
    window.addEventListener('message', handleIframeMessage);

    try {
        settings = await API.get('/settings');
        // Initialize show_borders based on borderless if not present
        if (settings.show_borders === undefined) {
            settings.show_borders = !settings.borderless;
        }
        originalSettings = JSON.parse(JSON.stringify(settings));
        isDirty = false;
        render(container, addNotice);
    } catch (error) {
        clear(container);
        container.appendChild(
            el('div', { class: 'presszone-comments-error' },
                el('h2', {}, __('Error loading settings', 'comments-press-zone')),
                el('p', {}, error.message)
            )
        );
        addNotice?.(error.message, 'error');
    }
}

/**
 * Render the design form.
 */
function render(container, addNotice) {
    clear(container);

    container.appendChild(
        el('div', { class: 'presszone-comments-settings' },
            // Header
            el('header', { class: 'presszone-comments-settings__header' },
                el('h1', {}, __('Design', 'comments-press-zone')),
                SaveButton(
                    saving ? __('Saving...', 'comments-press-zone') : __('Save Settings', 'comments-press-zone'),
                    () => saveSettings(container, addNotice),
                    saving,
                    {
                        padding: settings.padding,
                        styling: settings.styling,
                        attrs: { id: 'presszone-comments-save-design' }
                    }
                )
            ),

            // Settings Row at Top
            el('div', { class: 'presszone-comments-design-settings-row' },
                // Padding Select (small) + Show Borders Toggle
                el('div', { class: 'presszone-comments-design-setting presszone-comments-design-setting--small' },
                    Select(
                        'padding',
                        __('Padding', 'comments-press-zone'),
                        __('Adjust the spacing within comment items', 'comments-press-zone'),
                        [
                            { value: 'minimal', label: __('Minimal', 'comments-press-zone') },
                            { value: 'standard', label: __('Standard', 'comments-press-zone'), default: true },
                            { value: 'wide', label: __('Wide', 'comments-press-zone') }
                        ],
                        settings,
                        () => {
                            isDirty = true;
                            updateIframePreview();
                        },
                        null,
                        {
                            styling: settings.styling,
                            showBorders: settings.show_borders,
                            style: 'margin-bottom: 4px; display: flex; align-items: center; justify-content: space-between; gap: 10px;'
                        }
                    ),
                    // Modern Toggle for Borders
                    el('div', { style: 'margin-top: 4px;' },
                        Toggle(
                            'show_borders',
                            __('Show Borders', 'comments-press-zone'),
                            '', // No desc needed here
                            settings,
                            (key, value) => {
                                settings.borderless = !value;
                                isDirty = true;
                                toggleBorderThicknessVisibility(value);
                                updateIframePreview();
                            }
                        )
                    ),
                ),
                // Border Thickness Tiles (compact)
                el('div', {
                    id: 'border-thickness-wrapper',
                    class: 'presszone-comments-design-setting presszone-comments-design-setting--small'
                },
                    TileSelect(
                        'border_width',
                        __('Border Thickness', 'comments-press-zone'),
                        __('Set the thickness of comment borders', 'comments-press-zone'),
                        [
                            {
                                value: 'standard',
                                label: __('Standard', 'comments-press-zone'),
                                default: true,
                                preview: el('div', { class: 'presszone-comments-border-preview presszone-comments-border-preview--standard' })
                            },
                            {
                                value: 'thick',
                                label: __('Thick', 'comments-press-zone'),
                                preview: el('div', { class: 'presszone-comments-border-preview presszone-comments-border-preview--thick' })
                            },
                            {
                                value: 'extra-thick',
                                label: __('Extra Thick', 'comments-press-zone'),
                                preview: el('div', { class: 'presszone-comments-border-preview presszone-comments-border-preview--extra-thick' })
                            }
                        ],
                        settings,
                        () => {
                            isDirty = true;
                            updateIframePreview();
                        },
                        { layout: 'compact' }
                    )
                ),
                // Styling Tiles (large)
                el('div', { class: 'presszone-comments-design-setting presszone-comments-design-setting--large' },
                    TileSelect(
                        'styling',
                        __('Styling', 'comments-press-zone'),
                        __('Choose the border radius for comment elements', 'comments-press-zone'),
                        [
                            { value: 'square', label: __('Square', 'comments-press-zone') },
                            { value: 'rounded', label: __('Rounded', 'comments-press-zone'), default: true },
                            { value: 'pill', label: __('Pill', 'comments-press-zone') }
                        ],
                        settings,
                        () => {
                            isDirty = true;
                            updateIframePreview();
                        }
                    )
                ),
                // Colors Tiles (large)
                el('div', { class: 'presszone-comments-design-setting presszone-comments-design-setting--large' },
                    TileSelect(
                        'colors',
                        __('Colors', 'comments-press-zone'),
                        __('Select the color theme for the comments', 'comments-press-zone'),
                        [
                            {
                                value: 'inherit',
                                label: __('Inherit', 'comments-press-zone'),
                                default: true,
                                preview: el('div', { class: 'presszone-comments-color-preview presszone-comments-color-preview--inherit' })
                            },
                            {
                                value: 'light',
                                label: __('Light', 'comments-press-zone'),
                                preview: el('div', { class: 'presszone-comments-color-preview presszone-comments-color-preview--light' },
                                    el('div', { class: 'p-1' }), el('div', { class: 'p-2' }), el('div', { class: 'p-3' })
                                )
                            },
                            {
                                value: 'dark',
                                label: __('Dark', 'comments-press-zone'),
                                preview: el('div', { class: 'presszone-comments-color-preview presszone-comments-color-preview--dark' },
                                    el('div', { class: 'p-1' }), el('div', { class: 'p-2' }), el('div', { class: 'p-3' })
                                )
                            }
                        ],
                        settings,
                        () => {
                            isDirty = true;
                            updateIframePreview();
                        }
                    )
                )
            ),

            // Live Preview - Full Width at Bottom
            el('div', { class: 'presszone-comments-design-preview presszone-comments-design-preview--fullwidth' },
                el('div', { class: 'presszone-comments-design-preview__header' },
                    el('h3', {}, __('Live Preview', 'comments-press-zone')),
                    createDeviceControls()
                ),
                createPreviewIframe()
            )
        )
    );

    // Initialize border thickness visibility based on current settings
    toggleBorderThicknessVisibility(settings.show_borders);
}

/**
 * Detect if user is on a mobile device.
 */
function isMobileDevice() {
    return window.innerWidth <= 480 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Create device size controls.
 */
function createDeviceControls() {
    const controls = el('div', { class: 'presszone-comments-device-controls' });
    const isMobile = isMobileDevice();

    const devices = [
        { id: 'desktop', icon: '💻', width: '100%', label: __('Desktop', 'comments-press-zone') },
        { id: 'tablet', icon: '📱', width: '768px', label: __('Tablet', 'comments-press-zone') },
        { id: 'mobile', icon: '📱', width: '375px', label: __('Mobile', 'comments-press-zone') }
    ];

    // Auto-select mobile view on mobile devices
    const defaultDevice = isMobile ? 'mobile' : 'desktop';

    devices.forEach(device => {
        const isDefault = device.id === defaultDevice;
        const btn = el('button', {
            class: `presszone-comments-btn presszone-comments-btn--ghost device-btn ${isDefault ? 'active' : ''}`,
            title: device.label,
            onclick: () => {
                // Update iframe width
                if (previewIframe) {
                    previewIframe.style.width = device.width;
                    previewIframe.style.maxWidth = device.id === 'desktop' ? '100%' : device.width;
                }

                // Update active state
                controls.querySelectorAll('.device-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            }
        }, device.icon);

        // Auto-apply mobile width on mobile devices
        if (isDefault && isMobile && previewIframe) {
            previewIframe.style.width = device.width;
            previewIframe.style.maxWidth = device.width;
        }

        controls.appendChild(btn);
    });

    return controls;
}

/**
 * Create the preview iframe element wrapped in a container.
 */
function createPreviewIframe() {
    iframeReady = false;
    const url = (window.presszoneCommentsAdmin && window.presszoneCommentsAdmin.previewUrl) || '';

    // Create wrapper container for the iframe
    const wrapper = el('div', { class: 'presszone-comments-preview-frame-wrapper' });

    previewIframe = el('iframe', {
        src: url,
        id: 'presszone-comments-theme-preview',
        style: 'width: 100%; border: none; background: white;',
        title: __('Theme Preview', 'comments-press-zone')
    });

    wrapper.appendChild(previewIframe);

    return wrapper;
}

/**
 * Update the iframe preview and admin buttons with current settings.
 */
function updateIframePreview() {
    // 1. Update Preview Iframe
    if (iframeReady && previewIframe && previewIframe.contentWindow) {
        previewIframe.contentWindow.postMessage({
            type: 'presszone_update_settings',
            settings: settings
        }, window.location.origin);
    }

    /**
     * Helper to apply setting classes to elements.
     */
    const applySettingClasses = (el, prefix, includePadding = true) => {
        if (!el) return;

        // Remove existing setting classes
        Array.from(el.classList).forEach(cls => {
            if (cls.startsWith(`${prefix}--padding-`) ||
                cls.startsWith(`${prefix}--styling-`) ||
                cls.startsWith(`${prefix}--border-`)) {
                el.classList.remove(cls);
            }
        });

        // Add new classes
        if (includePadding && settings.padding) el.classList.add(`${prefix}--padding-${settings.padding}`);
        if (settings.styling) el.classList.add(`${prefix}--styling-${settings.styling}`);

        // Handle Border Width (maps to the new border thickness system)
        if (settings.border_width) {
            el.classList.add(`${prefix}--border-${settings.border_width}`);
        }

        // Toggle borderless/no-borders class
        const isBorderless = settings.show_borders === false;
        el.classList.toggle(`${prefix}--no-borders`, isBorderless);
        el.classList.toggle(`${prefix}--borderless`, isBorderless);
    };

    // 2. Update Admin "Save" Button live
    applySettingClasses(document.getElementById('presszone-comments-save-design'), 'presszone-comments-btn');

    // 3. Update Padding Select live - BUT ignore padding to prevent size jumping
    applySettingClasses(document.getElementById('padding'), 'presszone-comments-select', false);
    // Border width select doesn't exist as a DOM ID (it's a TileSelect), but keeping for safety/future
    applySettingClasses(document.getElementById('border_width'), 'presszone-comments-select', false);

    const wrapper = document.querySelector('.presszone-comments-preview-frame-wrapper');
    if (wrapper) {
        // Clear previous classes
        Array.from(wrapper.classList).forEach(cls => {
            if (cls.startsWith('presszone-comments-border--') ||
                cls.startsWith('presszone-comments-styling--') ||
                cls.startsWith('presszone-comments-padding--')) {
                wrapper.classList.remove(cls);
            }
        });

        // Apply frontend-style classes to the wrapper
        if (settings.border_width) wrapper.classList.add(`presszone-comments-border--${settings.border_width}`);
        if (settings.styling) wrapper.classList.add(`presszone-comments-styling--${settings.styling}`);
        if (settings.padding) wrapper.classList.add(`presszone-comments-padding--${settings.padding}`);
        if (settings.show_borders === false) wrapper.classList.add('presszone-comments-styling--borderless');
    }
}

async function saveSettings(container, addNotice) {
    saving = true;
    render(container, addNotice);

    console.log('[Design] Saving settings:', JSON.stringify(settings, null, 2));
    console.log('[Design] border_width value:', settings.border_width);

    try {
        await API.post('/settings', settings);
        originalSettings = JSON.parse(JSON.stringify(settings));
        isDirty = false;
        addNotice?.(__('Settings saved', 'comments-press-zone'), 'success');
        console.log('[Design] Settings saved successfully');
    } catch (error) {
        console.error('[Design] Save failed:', error);
        addNotice?.(error.message, 'error');
    } finally {
        saving = false;
        render(container, addNotice);
    }
}

export default { renderDesign };
